import {useSyncExternalStore} from "react";
import type {WinMode} from "../_types/LotoCardboard";

export type AppSettings = {
    theme: {
        primary: string;
        secondary: string;
        backgroundFrom: string;
        backgroundTo: string;
    };
    eventLogoDataUrl?: string;
    partyDefaultWinMode: WinMode;
    winModeLabels: Record<WinMode, string>;
    adminFlipMs: number;
    tv: {
        intermissionTitle: string;
        bannerMessage: string;
        overlayEnabled: boolean;
        overlayDurationMs: number;
        drawnNumberColor: string;
        lastNumberColor: string;
    };
};

export type AppSettingsPatch =
    Partial<Omit<AppSettings, "theme" | "tv" | "winModeLabels">> & {
        theme?: Partial<AppSettings["theme"]>;
        tv?: Partial<AppSettings["tv"]>;
        winModeLabels?: Partial<AppSettings["winModeLabels"]>;
    };

const STORAGE_KEY = "appSettings";
const CHANGED_EVENT = "appSettingsChanged";

const DEFAULTS: AppSettings = {
    theme: {primary: "#24b4ff", secondary: "#c39bff", backgroundFrom: "#090d1c", backgroundTo: "#0b1021"},
    eventLogoDataUrl: undefined,
    partyDefaultWinMode: "line1",
    winModeLabels: {
        line1: "1 ligne",
        line2: "2 lignes",
        full: "Carton plein",
    },
    adminFlipMs: 1500,
    tv: {
        intermissionTitle: "Entracte",
        bannerMessage: "",
        overlayEnabled: true,
        overlayDurationMs: 3500,
        drawnNumberColor: "#1b76cf",
        lastNumberColor: "#24b4ff",
    },
};

let cachedRaw: string | null = null;
let cachedSettings: AppSettings = DEFAULTS;

const isHexColor = (value: unknown): value is string =>
    typeof value === "string" && /^#([0-9a-f]{6}|[0-9a-f]{3})$/i.test(value.trim());

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export const readAppSettings = (): AppSettings => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === cachedRaw) return cachedSettings;
    if (!raw) {
        cachedRaw = null;
        cachedSettings = DEFAULTS;
        return cachedSettings;
    }
    try {
        const parsed = JSON.parse(raw) as Partial<AppSettings> | undefined;
        const theme = {
            primary: isHexColor(parsed?.theme?.primary) ? parsed!.theme!.primary : DEFAULTS.theme.primary,
            secondary: isHexColor(parsed?.theme?.secondary) ? parsed!.theme!.secondary : DEFAULTS.theme.secondary,
            backgroundFrom: isHexColor(parsed?.theme?.backgroundFrom) ? parsed!.theme!.backgroundFrom : DEFAULTS.theme.backgroundFrom,
            backgroundTo: isHexColor(parsed?.theme?.backgroundTo) ? parsed!.theme!.backgroundTo : DEFAULTS.theme.backgroundTo,
        };

        const partyDefaultWinMode: WinMode =
            parsed?.partyDefaultWinMode === "line1" || parsed?.partyDefaultWinMode === "line2" || parsed?.partyDefaultWinMode === "full"
                ? parsed.partyDefaultWinMode
                : DEFAULTS.partyDefaultWinMode;

        const winModeLabels = {
            line1: typeof parsed?.winModeLabels?.line1 === "string" && parsed.winModeLabels.line1.trim()
                ? parsed.winModeLabels.line1.trim()
                : DEFAULTS.winModeLabels.line1,
            line2: typeof parsed?.winModeLabels?.line2 === "string" && parsed.winModeLabels.line2.trim()
                ? parsed.winModeLabels.line2.trim()
                : DEFAULTS.winModeLabels.line2,
            full: typeof parsed?.winModeLabels?.full === "string" && parsed.winModeLabels.full.trim()
                ? parsed.winModeLabels.full.trim()
                : DEFAULTS.winModeLabels.full,
        } satisfies Record<WinMode, string>;

        const adminFlipMs = clamp(Number(parsed?.adminFlipMs ?? DEFAULTS.adminFlipMs), 200, 6000);

        const tv = {
            intermissionTitle:
                typeof parsed?.tv?.intermissionTitle === "string" && parsed.tv.intermissionTitle.trim()
                    ? parsed.tv.intermissionTitle.trim()
                    : DEFAULTS.tv.intermissionTitle,
            bannerMessage: typeof parsed?.tv?.bannerMessage === "string" ? parsed.tv.bannerMessage : DEFAULTS.tv.bannerMessage,
            overlayEnabled: typeof parsed?.tv?.overlayEnabled === "boolean" ? parsed.tv.overlayEnabled : DEFAULTS.tv.overlayEnabled,
            overlayDurationMs: clamp(Number(parsed?.tv?.overlayDurationMs ?? DEFAULTS.tv.overlayDurationMs), 800, 20000),
            drawnNumberColor: isHexColor(parsed?.tv?.drawnNumberColor) ? parsed!.tv!.drawnNumberColor : DEFAULTS.tv.drawnNumberColor,
            lastNumberColor: isHexColor(parsed?.tv?.lastNumberColor) ? parsed!.tv!.lastNumberColor : DEFAULTS.tv.lastNumberColor,
        };

        const eventLogoDataUrl =
            typeof parsed?.eventLogoDataUrl === "string" && parsed.eventLogoDataUrl.startsWith("data:")
                ? parsed.eventLogoDataUrl
                : undefined;

        cachedRaw = raw;
        cachedSettings = {
            theme,
            eventLogoDataUrl,
            partyDefaultWinMode,
            winModeLabels,
            adminFlipMs,
            tv,
        };
        return cachedSettings;
    } catch {
        cachedRaw = null;
        cachedSettings = DEFAULTS;
        return cachedSettings;
    }
};

export const writeAppSettings = (next: AppSettings) => {
    const raw = JSON.stringify(next);
    cachedRaw = raw;
    cachedSettings = next;
    localStorage.setItem(STORAGE_KEY, raw);
    window.dispatchEvent(new Event(CHANGED_EVENT));
};

export const updateAppSettings = (patch: AppSettingsPatch) => {
    const current = readAppSettings();
    const next: AppSettings = {
        ...current,
        ...patch,
        theme: {...current.theme, ...(patch.theme ?? {})},
        tv: {...current.tv, ...(patch.tv ?? {})},
        winModeLabels: {...current.winModeLabels, ...(patch.winModeLabels ?? {})},
    };
    writeAppSettings(next);
};

export const subscribeAppSettings = (callback: () => void) => {
    const onStorage = (e: StorageEvent) => {
        if (e.key === STORAGE_KEY) callback();
    };
    const onChanged = () => callback();
    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGED_EVENT, onChanged);
    return () => {
        window.removeEventListener("storage", onStorage);
        window.removeEventListener(CHANGED_EVENT, onChanged);
    };
};

export const useAppSettings = () =>
    useSyncExternalStore(subscribeAppSettings, readAppSettings, readAppSettings);
